import bus from './event_bus';
import { get, hasOwnProperty } from '@/lib/helpers';
import { isCustomerAuthenticatedSync } from '@/lib/login_utils';
import { doCtxLogout } from '@/store/actions/auth';
import { authState } from '@/store/getters/auth';
import { getLocalKey } from '@/store/local_store';
import { ACCESS_DETAILS } from '@/store/store_keys';

/**
 * One place that decides what an api answering 401 or 403 means: the token this
 * app is sending is no longer accepted, so the session is over and the customer
 * has to log in again.
 *
 * Before this, each caller made its own choice - some logged out, some showed a
 * generic error screen, most did nothing and left the customer on a screen whose
 * data never arrived. Every network path now funnels here instead:
 *
 *  - the so-fetch clients in api/fetch_helper.ts, through
 *    authFailureResponseInterceptor
 *  - the hand-rolled fetch calls, through fetchHandler in api/utils.ts
 *  - anything that lands in defaultCatch
 *
 * 403 is treated the same as 401 on purpose. The backend uses it for a token
 * that is valid but no longer entitled (revoked, or a customer record that has
 * been closed), and there is nothing the customer can do with that session
 * either.
 */
export const AUTH_FAILURE_STATUSES = [401, 403];

export function isAuthFailureStatus(status: any): boolean {
  return AUTH_FAILURE_STATUSES.includes(Number(status));
}

/**
 * Whether there is a session worth ending.
 *
 * This is the guard that keeps the login flow usable. The auth service answers
 * 401 for a wrong or expired otp, and a customer who has not logged in yet has
 * no session to lose - without this check that 401 would reroute them off the
 * otp screen back to the start. It doubles as the de-dupe: the first failure
 * clears the stored session, so the 401s from any calls that were already in
 * flight find nothing left to do.
 *
 * Reads the stored ACCESS_DETAILS as well as redux because the request headers
 * do the same - a call can go out authenticated on the stored token alone.
 */
function hasLiveSession(): boolean {
  if (isCustomerAuthenticatedSync(authState())) {
    return true;
  }
  const accessDetails: any = getLocalKey(ACCESS_DETAILS);
  return Boolean(
    accessDetails &&
      hasOwnProperty(accessDetails, 'access_token') &&
      accessDetails.access_token
  );
}

let logoutInFlight = false;

/**
 * Ends the session the way the rest of the app already ends an expired one:
 * drop the loader, report it, tell the customer why, and hand them to the login
 * screen. doCtxLogout is called with shouldCallApi = false - /logout would only
 * answer 401 as well.
 */
export function logoutOnAuthFailure(context = ''): void {
  if (logoutInFlight || !hasLiveSession()) {
    return;
  }
  logoutInFlight = true;
  console.log(
    `Signing out, the api rejected our token${context ? ` (${context})` : ''}`
  );
  bus.$emit('loader');
  bus.$emit('syncEvent', 'WEB.SESSION.EXPIRED', { nonInteraction: true });
  bus.$emit('notify', {
    type: 'warning',
    message: 'Session expired. Login again.',
    autoClose: 4000,
    showClose: true,
  });
  try {
    Promise.resolve(doCtxLogout(false)).catch((err) =>
      console.log('Error while signing out after an auth failure', err)
    );
  } finally {
    // Released once the current task finishes rather than left latched, so a
    // 401 in a later session still signs that session out. By then the session
    // is already cleared, so hasLiveSession keeps the duplicates out.
    setTimeout(() => {
      logoutInFlight = false;
    });
  }
}

/**
 * @returns true when the status was an auth failure and a logout was triggered,
 * so a caller can stop handling the response.
 */
export function handleAuthFailure(status: any, context = ''): boolean {
  if (!isAuthFailureStatus(status)) {
    return false;
  }
  logoutOnAuthFailure(context);
  return true;
}

/**
 * Response interceptor for the so-fetch clients. so-fetch runs these on failed
 * responses too, before it throws, so this sees the 401 first and the caller's
 * own catch block still runs afterwards.
 */
export const authFailureResponseInterceptor = (response: any) => {
  handleAuthFailure(
    get(response, 'status', 0),
    get(response, 'config.url', '') || get(response, 'url', '')
  );
  return response;
};

/* -------------------------------------------------------------------------- */
/* The auth failure the browser refuses to show us                            */
/* -------------------------------------------------------------------------- */

/**
 * Everything above assumes we get to read the 401. In qa and production we do
 * not.
 *
 * qa-rest.moneytap.com (and app.moneytap.com) drop the
 * Access-Control-Allow-Origin header on exactly one kind of response: a 401
 * produced by the bearer token filter. Verified against qa-rest on 2026-09-25:
 *
 *   OPTIONS preflight               200, header present
 *   request with no Authorization   401, header present
 *   request with a bad Authorization 401, header MISSING
 *
 * The app is served from qa-web.moneytap.com / web.moneytap.com, so every api
 * call is cross origin. Without that header the browser will not hand the
 * response to javascript at all - fetch rejects with a TypeError that carries no
 * status, and so-fetch has no catch of its own, so the raw TypeError travels
 * straight to the caller. The response interceptor never runs. fetchHandler
 * never runs. handleAuthFailure is never reached, and the customer sits on a
 * dead screen - which is the bug that survived the last fix.
 *
 * It looks like an application bug because devtools still prints "401" in red:
 * devtools reports what the browser received, not what the page was allowed to
 * see. It also only happens off localhost - local-setup/proxy.js adds the header
 * to every response, so the same build behaves correctly through the proxy.
 *
 * The real fix belongs in the backend cors filter. Until it lands, this reads
 * the two things we still have when the response is taken away from us: the
 * token we sent, and whether the api answers at all when we ask without it.
 */

/**
 * Chrome and Android WebView say "Failed to fetch", Safari and iOS WKWebView
 * "Load failed", Firefox "NetworkError when attempting to fetch resource".
 */
const NETWORK_ERROR_MESSAGE =
  /failed to fetch|load failed|networkerror|network request failed/i;

/**
 * True only for the rejection a blocked response produces: a TypeError whose
 * message is one of the wordings a browser uses when the request never
 * completed, and with no status on it.
 *
 * Deliberately matched on the message and not on `instanceof TypeError` alone.
 * defaultCatch is the catch block for the whole promise chain, so an ordinary
 * bug in a caller's own .then() - "cannot read properties of undefined" is a
 * TypeError too - would otherwise reach here and sign a working customer out.
 * A wording we do not recognise simply falls through and nothing happens, which
 * is the safe way round.
 */
export function isBlockedResponseError(err: any): boolean {
  if (!err) {
    return false;
  }
  if (Number(err.status)) {
    return false;
  }
  return NETWORK_ERROR_MESSAGE.test(String(err.message || ''));
}

/** The token the request interceptors would have sent - redux first, then storage. */
function currentAccessToken(): string {
  const fromState = get(authState(), 'access_token', '');
  if (fromState) {
    return fromState;
  }
  const accessDetails: any = getLocalKey(ACCESS_DETAILS);
  return get(accessDetails, 'access_token', '') || '';
}

/** The jwt payload, or null when the token is not a readable jwt any more. */
function decodeJwtPayload(token: string): any {
  const parts = String(token).split('.');
  if (parts.length !== 3) {
    return null;
  }
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    const binary = window.atob(padded);
    let json = binary;
    try {
      // The payload can hold non ascii (a name, an address), which atob leaves
      // as raw bytes - decode them before JSON.parse chokes on them.
      json = new TextDecoder().decode(
        Uint8Array.from(binary, (char) => char.charCodeAt(0))
      );
    } catch (decodeErr) {
      // No TextDecoder, or nothing to decode - the ascii payload is fine as is.
    }
    return JSON.parse(json);
  } catch (err) {
    return null;
  }
}

/** A token is only a few seconds from useless once it is this close to exp. */
const CLOCK_SKEW_MS = 30 * 1000;

type StoredTokenVerdict = 'dead' | 'alive' | 'unknown';

/**
 * What the token itself says, without asking the backend.
 *
 * 'dead'    - gone, unreadable, or past its exp. Nothing the api could have
 *             answered but a rejection, so no probe is needed.
 * 'alive'   - exp is still in the future. The token looks fine, so a failed call
 *             is as likely to be a lift or a tunnel as a dead session.
 * 'unknown' - a readable token with no exp claim. Treated like 'alive': ask.
 */
function readStoredTokenVerdict(): StoredTokenVerdict {
  const token = currentAccessToken();
  if (!token) {
    return 'dead';
  }
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return 'dead';
  }
  const exp = Number(payload.exp);
  if (!exp) {
    return 'unknown';
  }
  return Date.now() + CLOCK_SKEW_MS >= exp * 1000 ? 'dead' : 'alive';
}

const PROBE_TIMEOUT_MS = 6000;
let probeInFlight: Promise<boolean> | null = null;

/**
 * Asks the same url again with no Authorization header.
 *
 * That is the one request the backend does answer with a cors header, so if the
 * browser can read this answer the network is up, the host is up and cors is
 * working - and the only thing wrong with the call that just failed was the
 * token we put on it. If this fails too, the customer has genuinely lost the
 * network and signing them out would be wrong.
 *
 * Always a GET, whatever the failed call was, so probing can have no side
 * effect: a GET to a POST-only endpoint still answers 401 or 405, and either is
 * enough to prove the api is reachable.
 */
function isApiReachableWithoutToken(url: string): Promise<boolean> {
  // Absolute urls only. A relative one would be answered by the web server that
  // served the app, which says nothing about whether the api is up.
  if (!/^https?:\/\//i.test(url)) {
    return Promise.resolve(false);
  }
  if (probeInFlight) {
    return probeInFlight;
  }
  const controller =
    typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(() => {
    if (controller) {
      controller.abort();
    }
  }, PROBE_TIMEOUT_MS);
  probeInFlight = fetch(url, {
    method: 'GET',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
    ...(controller ? { signal: controller.signal } : {}),
  })
    .then(() => true)
    .catch(() => false)
    .then((reachable: boolean) => {
      clearTimeout(timer);
      probeInFlight = null;
      return reachable;
    });
  return probeInFlight;
}

/**
 * The entry point for a rejected fetch, alongside handleAuthFailure for a
 * readable response. Decides whether a rejection with no status was really the
 * 401 the browser hid, and signs out if so.
 *
 * @param url the url that failed, used to probe the same host. Falls back to the
 * api root, which answers the same way.
 */
export function handleAuthFailureError(err: any, url = ''): void {
  if (!isBlockedResponseError(err) || logoutInFlight || !hasLiveSession()) {
    return;
  }
  if (readStoredTokenVerdict() === 'dead') {
    logoutOnAuthFailure(`${url || 'request'} - token no longer valid`);
    return;
  }
  // The token still claims to be good, so this could equally be a real network
  // drop. Only the backend can settle it, so ask.
  isApiReachableWithoutToken(
    url || process.env.VUE_APP_API_BASE_URL || ''
  ).then((reachable: boolean) => {
    if (reachable) {
      logoutOnAuthFailure(`${url || 'request'} - api rejected our token`);
    }
  });
}
